/**
 * App-wide configuration constants.
 *
 * Rewritten for the enterprise build: the repeated `(import.meta as any).env`
 * casts are replaced by typed env accessors (`envInt` / `envBool` / `envStr`)
 * so every value is read the same way and `NaN`/empty-string footguns are
 * handled in one place. All exported names and their runtime values are
 * preserved exactly — downstream code reading `STORAGE_KEYS.TOKEN`,
 * `CAMERA_CONFIG.IDEAL_WIDTH`, etc. is unaffected.
 */

/* -------------------------------------------------------------------------- */
/*  Typed environment accessors                                               */
/* -------------------------------------------------------------------------- */

type ViteEnv = Record<string, string | boolean | undefined>;

/** Vite injects `import.meta.env` at build time; guard for non-Vite contexts (tests/SSR). */
const ENV: ViteEnv = ((): ViteEnv => {
  try {
    return ((import.meta as unknown as { env?: ViteEnv }).env) ?? {};
  } catch {
    return {};
  }
})();

/** Read a string env var, falling back when unset or empty. */
function envStr(key: string, fallback: string): string {
  const raw = ENV[key];
  return typeof raw === 'string' && raw.length > 0 ? raw : fallback;
}

/** Read an integer env var, falling back when unset or unparseable. */
function envInt(key: string, fallback: number): number {
  const raw = ENV[key];
  if (typeof raw !== 'string') return fallback;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/** Read a boolean env var (true only for the literal string "true"). */
function envBool(key: string): boolean {
  return ENV[key] === 'true' || ENV[key] === true;
}

const IS_DEV = ENV.DEV === true || ENV.DEV === 'true';

// API Configuration
export const API_CONFIG = {
  TIMEOUT: envInt('VITE_API_TIMEOUT', 10000),
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  UPLOAD_TIMEOUT: 30000,
} as const;

// File Upload Limits
export const FILE_LIMITS = {
  MAX_FILE_SIZE: envInt('VITE_MAX_FILE_SIZE', 10 * 1024 * 1024), // 10MB
  MAX_IMAGE_SIZE: envInt('VITE_MAX_FILE_SIZE', 10 * 1024 * 1024), // 10MB
  MAX_AUDIO_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_IMAGE_TYPES: envStr('VITE_ALLOWED_FILE_TYPES', 'image/jpeg,image/png,image/webp').split(','),
  ALLOWED_AUDIO_TYPES: ['audio/wav', 'audio/mp3', 'audio/m4a', 'audio/ogg'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'text/plain'],
} as const;

// Image Processing
export const IMAGE_CONFIG = {
  MIN_WIDTH: 100,
  MIN_HEIGHT: 100,
  MAX_WIDTH: 8000,
  MAX_HEIGHT: 8000,
  QUALITY: 0.9,
  FORMAT: 'image/jpeg',
  WATERMARK_OPACITY: 0.7,
  WATERMARK_POSITION: 'bottom-right',
} as const;

// Enhanced Arrow Configuration
export const ARROW_CONFIG = {
  SIZE: 40,
  COLOR: '#FFD700',
  BORDER_COLOR: '#000000',
  BORDER_WIDTH: 2,
  SHADOW_COLOR: 'rgba(0, 0, 0, 0.8)',
  SHADOW_BLUR: 4,
  ARROW_LENGTH: 25,
  ARROW_WIDTH: 8,
  MIN_SIZE: 20,
  MAX_SIZE: 80,
} as const;

// Enhanced Watermark Configuration
export const WATERMARK_CONFIG = {
  FONT_FAMILY: '"Courier New", Courier, monospace',
  FONT_SIZE: 48,
  COLOR: 'rgba(255, 215, 0, 0.7)',
  BACKGROUND_COLOR: 'rgba(0, 0, 0, 0.5)',
  PADDING: 10,
  BORDER_RADIUS: 5,
  POSITIONS: {
    'top-left': { x: 0, y: 0, anchor: 'start' },
    'top-right': { x: 1, y: 0, anchor: 'end' },
    'bottom-left': { x: 0, y: 1, anchor: 'start' },
    'bottom-right': { x: 1, y: 1, anchor: 'end' },
    'center': { x: 0.5, y: 0.5, anchor: 'center' },
  },
} as const;

// Camera Configuration
export const CAMERA_CONFIG = {
  // Capture at 4K (UHD) by default. These are `ideal` getUserMedia constraints,
  // so a device that can't do 4K degrades gracefully to its max — never an
  // error. Higher capture resolution is the ONLY way to put more real detail
  // into the small watermark plan boxes (location/site): those boxes are a
  // fixed fraction of the photo, so doubling the photo's pixels roughly doubles
  // the box's pixels (a portrait location box ~350px → ~700px wide). A 4K photo
  // + watermark strip stays under the iOS canvas caps (MAX_CANVAS_AREA /
  // MAX_CANVAS_SIDE in utils/compat), so the export is not downscaled back.
  // Tunable down via VITE_CAMERA_WIDTH/HEIGHT if file size becomes a concern.
  IDEAL_WIDTH: envInt('VITE_CAMERA_WIDTH', 3840),
  IDEAL_HEIGHT: envInt('VITE_CAMERA_HEIGHT', 2160),
  FACING_MODE: {
    ENVIRONMENT: 'environment',
    USER: 'user',
  },
  TIMEOUT: envInt('VITE_CAMERA_TIMEOUT', 10000),
  RETRY_DELAY: 2000,
  MAX_RETRIES: 50,
  RETRY_INTERVAL: 100,
} as const;

// GPS Configuration
export const GPS_CONFIG = {
  TIMEOUT: envInt('VITE_GPS_TIMEOUT', 10000),
  MAX_AGE: 60000,
  HIGH_ACCURACY: envBool('VITE_GPS_HIGH_ACCURACY'),
  ENABLE_HIGH_ACCURACY: envBool('VITE_GPS_HIGH_ACCURACY'),
} as const;

// UI Configuration
export const UI_CONFIG = {
  ANIMATION_DURATION: 300,
  DEBOUNCE_DELAY: 300,
  THROTTLE_DELAY: 100,
  TOAST_DURATION: {
    SUCCESS: 4000,
    ERROR: 6000,
    WARNING: 5000,
    INFO: 4000,
  },
  SCREEN_BREAKPOINTS: {
    MOBILE: 768,
    TABLET: 1024,
    DESKTOP: 1280,
  },
} as const;

// Authentication
export const AUTH_CONFIG = {
  TOKEN_EXPIRY: 24 * 60 * 60 * 1000, // 24 hours
  REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  SETTINGS: 'app_settings',
  FLOOR_PLAN: 'floorPlanImage',
  THEME: 'app_theme',
  LANGUAGE: 'app_language',
  CAMERA_SETTINGS: 'camera_settings',
  GPS_SETTINGS: 'gps_settings',
} as const;

// Screen Names
export const SCREENS = {
  LOGIN: 'login',
  PROJECTS: 'projects',
  PROJECT_PHOTOS: 'project-photos',
  CAMERA: 'camera',
  POSITIONING: 'positioning',
  RESULT: 'result',
  COMMENT: 'comment',
  EDIT_PHOTO: 'edit-photo',
  TEMPLATES: 'templates',
  MAP_CHOICE: 'map-choice',
  MAP_FLOOR_PLAN_CHOICE: 'map-floor-plan-choice',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK: {
    CONNECTION_FAILED: 'Unable to connect to server. Please check your internet connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    SERVER_ERROR: 'Server error occurred. Please try again later.',
    NOT_FOUND: 'The requested resource was not found.',
  },
  AUTH: {
    INVALID_CREDENTIALS: 'Invalid email or password',
    TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    LOGIN_FAILED: 'Login failed. Please check your credentials and try again.',
  },
  CAMERA: {
    PERMISSION_DENIED: 'Camera permission denied. Please allow camera access in your browser settings.',
    NOT_FOUND: 'No camera found. Please connect a camera and try again.',
    IN_USE: 'Camera is already in use by another application.',
    NOT_SUPPORTED: 'Camera is not supported in this browser.',
    INITIALIZATION_FAILED: 'Failed to initialize camera. Please try again.',
  },
  GPS: {
    PERMISSION_DENIED: 'Location permission denied. Please allow location access in your browser settings.',
    UNAVAILABLE: 'Location information is unavailable.',
    TIMEOUT: 'Location request timed out.',
    NOT_SUPPORTED: 'Geolocation is not supported by this browser.',
  },
  FILE: {
    TOO_LARGE: 'File size must be less than 10MB',
    INVALID_TYPE: 'Please select a valid file type',
    UPLOAD_FAILED: 'File upload failed. Please try again.',
    PROCESSING_FAILED: 'File processing failed. Please try again.',
  },
  VALIDATION: {
    REQUIRED_FIELD: 'This field is required',
    INVALID_EMAIL: 'Please enter a valid email address',
    INVALID_PASSWORD: 'Password must be at least 6 characters long',
    INVALID_FORMAT: 'Please enter a valid format',
  },
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Login successful!',
  LOGOUT: 'Logged out successfully',
  PHOTO_SAVED: 'Photo saved successfully!',
  PHOTO_UPLOADED: 'Photo uploaded successfully!',
  PROJECT_CREATED: 'Project created successfully!',
  PROJECT_DELETED: 'Project deleted successfully!',
  FLOOR_PLAN_UPLOADED: 'Floor plan uploaded successfully!',
  VOICE_COMMENT_SAVED: 'Voice comment saved!',
  SETTINGS_SAVED: 'Settings saved successfully!',
  PHOTO_PROCESSING_COMPLETE: 'Photo processing complete!',
} as const;

// Theme Configuration
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  X_MEN: 'x-men',
  SYSTEM: 'system',
} as const;

// Language Configuration
export const LANGUAGES = {
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  DE: 'de',
  IT: 'it',
} as const;

// Default Values
export const DEFAULTS = {
  THEME: THEMES.X_MEN,
  LANGUAGE: LANGUAGES.EN,
  CAMERA_FACING_MODE: CAMERA_CONFIG.FACING_MODE.ENVIRONMENT,
  PIN_POSITION: { x: 50, y: 50 },
  PIN_DIRECTION: 0,
  MAP_ZOOM: 1,
  MAP_PAN: { x: 0, y: 0 },
} as const;

/**
 * Resolve the API base URL.
 *
 * On Render (and any non-localhost host) the frontend and backend share a
 * domain, so relative URLs (`''`) are correct and avoid CORS entirely. Only
 * local development needs the explicit `http://localhost:3001` origin. An
 * explicit `VITE_API_BASE_URL` always wins.
 */
function resolveApiBaseUrl(): string {
  const explicit = envStr('VITE_API_BASE_URL', '');
  if (explicit) return explicit;

  if (typeof window !== 'undefined' && window.location) {
    const { hostname } = window.location;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return ''; // Relative URLs — correct for same-origin production (Render).
    }
  }
  return 'http://localhost:3001';
}

// Development Configuration
export const DEV_CONFIG = {
  DEBUG_MODE: IS_DEV,
  LOG_LEVEL: IS_DEV ? 'debug' : 'error',
  API_BASE_URL: resolveApiBaseUrl(),
  ENABLE_MOCK_DATA: false,
  ENABLE_DEBUG_PANEL: envBool('VITE_ENABLE_DEBUG_PANEL'),
} as const;

export default {
  API_CONFIG,
  FILE_LIMITS,
  IMAGE_CONFIG,
  CAMERA_CONFIG,
  GPS_CONFIG,
  UI_CONFIG,
  AUTH_CONFIG,
  STORAGE_KEYS,
  SCREENS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  THEMES,
  LANGUAGES,
  DEFAULTS,
  DEV_CONFIG,
};
